/* eslint-disable */
import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { cloneDeep } from 'lodash';
import { Button, Menu, MenuItem, Switch } from '@mui/material';
import { ChevronDown, Download, Upload, FileEdit } from 'lucide-react';
import { formActions, modalActions } from '../../../Actions';
import MultiRecipeEditModal from '@/Components/Modals/MultiRecipeEditModal/MultiRecipeEditModal';
import {
	handleResponseStreamDownload,
	inputHelper,
	isEmpty,
} from '../../../Helpers';
import { CustomizationListTable } from '../../../Components/Forms/Customizations/CustomizationListTable';
import cookingRecipesServices from '@/Services/Form Services/cookingRecipes.services';
import { notificationServices } from '@/Services';
import drawerActions from '@/Actions/drawer.actions';
import ExportFiltersDrawer from '@/Components/Drawers/ExportFiltersDrawer/ExportFiltersDrawer';
import { BackdropLoader } from '@/Components/Loaders';
import { useAuth } from '@/Components/Auth/Login/auth';
import userRolesAndPermissions from '@/Services/userRolesAndPermissions.services';
import authActions from '@/Actions/auth.actions';
import UploadDataModal from '@/Components/Modals/UploadDataModal/UploadDataModal';
import { AlertModal } from '@/Components/Modals';

const TABLE_COLUMNS = [
	{
		title: 'Name',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Brand',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Veg',
		config: {
			type: 'DATA',
		},
	},
];

const SPECIAL_COLUMS = [
	{
		title: 'Current State',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Customization Enabled',
		config: {
			type: 'UPDATE',
		},
	},
	{
		title: 'Version',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Recipe Management',
		config: {
			type: 'UPDATE',
		},
	},
	{
		title: 'Cooking/Kit Required',
		config: {
			type: 'DATA',
		}
	},
	{
		title: 'Recipe Status',
		config: {
			type: 'DATA',
		}
	}
];

const ACTION_COLUMNS = [
	{
		title: 'View',
		config: {
			type: 'VIEW',
		},
	},
];

const COLMAP = {
	Name: 'productName',
	Brand: 'brandName',
	Veg: 'isVeg',
	'Customization Enabled': 'isCustomizationEnabled',
	'Recipe Management': 'isCMSEnabled',
	'Cooking/Kit Required': 'isCookingEnabled',
	'Model Version': 'version',
};

// CSV Template headers as per backend requirements
const RECIPE_CSV_TEMPLATE_HEADERS = [
	'Parent Client Variant Id',
	'Parent Product Name',
	'Parent Batch Size',
	'Child Client Variant Id',
	'Child Product Name',
	'Child Qty',
];

const initialState = [];
const CookingRecipeListPage = (props) => {
	const {
		fetchProducts,
		toggleFlag,
		data,
		query,
		toggleAlert,
		toggleExportFiltersDrawer,
		stores,
		fetchStoresList,
		toggleUploadDataModal,
		toggleMultiRecipeEditModal,
	} = props;

	const [state, setState] = useState(initialState);
	const [recipeStatus, setRecipeStatus] = useState('ALL');
	const [numberOfSubmissions, setNumberOfSubmissions] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [importMenuAnchor, setImportMenuAnchor] = useState(null);
	const { userConfig = {} } = useAuth();
	const { level } = userConfig?.userDetails || {};

	const isImportMenuOpen = Boolean(importMenuAnchor);

	useEffect(() => {
		fetchStoresList({
			entity: 'FILTERED_STORES',
			req: {
				params: {
					id: '',
					type: '',
				},
			},
			method: 'LIST',
		});
	}, []);

	const handleEnable = (e) => {
		const { fieldName, fieldValue, dataset } = inputHelper(e);

		const clonedState = cloneDeep(state);

		const foundIndex = data.findIndex((obj) => {
			return obj.id === parseInt(dataset.id, 10);
		});
		clonedState[foundIndex][fieldName] = fieldValue;

		let entity = '';
		let req = {};
		let method = '';

		switch (fieldName) {
			case 'isCustomizationEnabled':
				entity = 'CUSTOMIZATIONS';
				req = {
					params: {
						productId: dataset.id,
					},
					data: {
						isCustomizationEnabled: fieldValue,
					},
				};
				method = 'PATCH';
				break;
			case 'isCMSEnabled':
				entity = 'RECIPES';
				req = {
					params: {
						productId: dataset.id,
					},
					data: {
						isCMSEnabled: fieldValue,
					},
				};
				method = 'PATCH';
				break;
			default:
				break;
		}
		toggleFlag({
			entity,
			req,
			method,
			promisify: true,
		})
			.then((res) => {
				if (res.status === 'Success') {
					setNumberOfSubmissions(numberOfSubmissions + 1);
					setState(clonedState);
				}
			})
			.catch(() => {
				// eslint-disable-next-line no-console
			});
	};
	const handleDelete = (productId) => {
		toggleAlert(true, {
			message: 'Click Proceed to delete product recipe',
			heading: 'Delete Product',
			productId,
		});
	};

	const SPECIAL_ITEMS = {
		Veg: (dataObj) => {
			const { isVeg } = dataObj;
			return (
				<div
					className={`dot-container small ${isVeg ? 'veg' : 'non-veg'
						}`}
				>
					<div className='dot' />
				</div>
			);
		},
		Version: (dataObj) => {
			const { version } = dataObj;
			return (
				<div>
					<div>v{version}</div>
				</div>
			);
		},
		'Customization Enabled': (dataObj, alt) => {
			const { id, isCustomizationEnabled } = dataObj;
			const { isDisabled } = alt;
			return (
				<Switch
					name='isCustomizationEnabled'
					value='isEnabled'
					size='small'
					color='primary'
					onChange={handleEnable}
					checked={isCustomizationEnabled}
					disabled={isDisabled}
					disableRipple={isDisabled}
					inputProps={{
						'data-id': id,
						'data-type': 'CUSTOMIZATIONS',
					}}
				/>
			);
		},
		'Recipe Management': (dataObj, alt) => {
			const { id, isCMSEnabled } = dataObj;
			const { isDisabled } = alt;
			return (
				<Switch
					name='isCMSEnabled'
					value='isEnabled'
					size='small'
					color='primary'
					onChange={handleEnable}
					checked={isCMSEnabled}
					disabled={isDisabled}
					disableRipple={isDisabled}
					inputProps={{
						'data-id': id,
						'data-type': 'RECIPE',
					}}
				/>
			);
		},
		'Current State': (dataObj, alt) => {
			const { text } = alt;
			return <div>{text}</div>;
		},
		'Cooking/Kit Required': (dataObj) => {
			return (
				<Switch
					name='isCookingEnabled'
					size='small'
					color='primary'
					checked={!!dataObj?.isCookingEnabled}
					disabled
					disableRipple
				/>
			);
		},
		'Recipe Status': (dataObj) => {
			if (dataObj?.recipeStatus) {
				return (
					<div className='text-xs font-semibold px-2 py-1 bg-gray-100 text-gray-600 rounded w-full'>
						{dataObj?.recipeStatus}
					</div>
				);
			}
			return <div className='w-full flex items-center justify-center'> - </div>;
		},
	};

	useEffect(() => {
		fetchProducts({
			entity: 'RECIPES',
			req: {
				params: {
					id: '',
					recipeStatus: recipeStatus === 'ALL' ? '' : recipeStatus,
				},
			},
			showNotification: true,
			method: 'LIST',
		});
	}, [recipeStatus]);

	useEffect(() => {
		if (Array.isArray(data) && numberOfSubmissions === 0) {
			setState(data);
		}
	}, [data]);

	const handleExport = async (filters) => {
		setIsLoading(true);
		try {
			let response;
			if (filters?.exportLevel === 'RM_LEVEL') {
				response = await cookingRecipesServices.exportCookingRecipes({
					req: {
						data: {
							filters,
						},
					},
				});
			} else {
				response = await cookingRecipesServices.exportCookingRecipesSfg(
					{
						req: {
							data: {
								filters,
							},
						},
					}
				);
			}
			if (response.status === 200) {
				const selectedStore = stores?.find((ele) => {
					return ele?.id === filters?.storeId;
				});
				handleResponseStreamDownload(
					response,
					`COOKING_RECIPES_${selectedStore?.name?.toUpperCase()}.csv`
				);
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: 'Some error occurred',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error,
			});
		} finally {
			setIsLoading(false);
		}
	};
	const openExportDrawer = () => {
		toggleExportFiltersDrawer(true, {
			heading: 'Export Cooking Recipes',
			// note: 'Filters are disabled for now, they will be LIVE Soon!',
			onConfirm: (args) => {
				const { filtersState } = args ?? {};
				handleExport(filtersState);
			},
			filterData: [
				{
					type: 'singleSelect',
					dependentOn: -1,
					compData: stores
						?.filter((ele) => {
							return ele?.type === 'BASE';
						})
						?.map((ele) => {
							return {
								id: ele?.id,
								label: ele?.name,
								title: ele?.name,
								value: ele?.id,
							};
						}),
					selectBy: 'id',
					label: 'Base Store',
					selectedValueKey: 'storeId',
					dateFormat: null,
					disabled: false,
					defaultState: null,
					mandatory: true,
				},
				{
					type: 'singleSelect',
					dependentOn: -1,
					compData: [
						{
							id: 'RM Level',
							label: 'RM Level',
							title: 'RM Level',
							value: 'RM_LEVEL',
						},
						{
							id: 'SFG Level',
							label: 'Direct SFG Level',
							title: 'Direct SFG Level',
							value: 'SFG_LEVEL',
						},
					],
					selectBy: 'id',
					label: 'Export Level',
					selectedValueKey: 'exportLevel',
					dateFormat: null,
					disabled: false,
					defaultState: 'RM_LEVEL',
					mandatory: true,
				},
			],
		});
	};

	// Import menu handlers
	const handleImportMenuOpen = (event) => {
		setImportMenuAnchor(event.currentTarget);
	};

	const handleImportMenuClose = () => {
		setImportMenuAnchor(null);
	};

	// Download CSV template
	const handleDownloadTemplate = useCallback(() => {
		handleImportMenuClose();
		const csvContent = RECIPE_CSV_TEMPLATE_HEADERS.join(',');
		const blob = new Blob([csvContent], { type: 'text/csv' });
		const downloadLink = document.createElement('a');
		downloadLink.href = window.URL.createObjectURL(blob);
		downloadLink.download = 'recipe_upload_template.csv';
		downloadLink.click();
	}, []);

	const handleFileChange = async (value) => {
		const file = value?.file;
		if (!file) return;

		try {
			setIsLoading(true);
			const response = await cookingRecipesServices.uploadRecipesCSV({
				req: {
					data: {
						csvData: file,
					},
				},
			});

			if (response?.status === 'Success' || response?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message || `Successfully processed ${response?.data?.recipesProcessed || 0} recipes`,
				});
				toggleUploadDataModal(false);
				// Refresh the recipes list
				fetchProducts({
					entity: 'RECIPES',
					req: {
						params: {
							id: '',
							recipeStatus: recipeStatus === 'ALL' ? '' : recipeStatus,
						},
					},
					showNotification: false,
					method: 'LIST',
				});
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.message || 'Error uploading recipes',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || 'Error uploading recipes CSV',
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleUploadClick = () => {
		handleImportMenuClose();
		toggleUploadDataModal(true);
	};

	const handleOpenMultiRecipeEdit = () => {
		toggleMultiRecipeEditModal(true, {
			externalClose: (success) => {
				if (success) {
					fetchProducts({
						entity: 'RECIPES',
						req: {
							params: {
								id: '',
								recipeStatus: recipeStatus === 'ALL' ? '' : recipeStatus,
							},
						},
						showNotification: false,
						method: 'LIST',
					});
				}
			},
		});
	};

	const renderCustomContainer = () => {
		return (
			<div className='flex justify-end gap-2 items-center gap-4 px-4 py-2'>
				<Button
					variant='outlined'
					color='primary'
					onClick={handleOpenMultiRecipeEdit}
					startIcon={<FileEdit className='size-4' />}
				>
					Upload Recipes
				</Button>
				<select
					value={recipeStatus}
					onChange={(e) => setRecipeStatus(e.target.value)}
					className='px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer shadow-sm font-medium text-gray-700 transition duration-150 ease-in-out hover:bg-gray-50'
				>
					<option value='ALL'>All Statuses</option>
					<option value='APPROVED'>Approved</option>
					<option value='APPROVAL_PENDING'>Approval Pending</option>
				</select>
				<Button
					variant='contained'
					color='primary'
					onClick={handleImportMenuOpen}
					endIcon={<ChevronDown className='size-4' />}
				>
					<Upload className='size-4 mr-1' />
					Import Recipes
				</Button>
				<Menu
					anchorEl={importMenuAnchor}
					open={isImportMenuOpen}
					onClose={handleImportMenuClose}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
				>
					<MenuItem onClick={handleDownloadTemplate}>
						<Download className='size-4 mr-2' />
						Download Template
					</MenuItem>
					<MenuItem onClick={handleUploadClick}>
						<Upload className='size-4 mr-2' />
						Upload CSV
					</MenuItem>
				</Menu>
			</div>
		);
	};

	return (
		<div className='list-page-root'>
			<BackdropLoader isLoading={isLoading} showProgress />
			<ExportFiltersDrawer />
			<AlertModal />
			<MultiRecipeEditModal />
			<UploadDataModal handleImport={handleFileChange} />
			<CustomizationListTable
				entity='RECIPE'
				colMap={COLMAP}
				columns={TABLE_COLUMNS}
				hideRecipeCreation={level === 1 || level === 3}
				data={state.filter((product) => {
					return (
						product.productName
							.toLowerCase()
							.indexOf(query.trim().toLowerCase()) !== -1 &&
						product?.skuType !== 'INGREDIENT'
					);
				})}
				actions={[...SPECIAL_COLUMS, ...ACTION_COLUMNS]}
				special={SPECIAL_ITEMS}
				handleDelete={handleDelete}
				onExportClick={openExportDrawer}
				showContainer={true}
				renderContainer={renderCustomContainer}
			/>
		</div>
	);
};

const mapStateToProps = (state) => {
	const data = state.form.RECIPES.data.LIST;
	const { userPermissions, navState } = state.navigation;
	const stores = state?.form?.FILTERED_STORES?.data?.LIST;
	const { userList } = state?.auth;

	return {
		userPermissions,
		data,
		stores,
		navState,
		userList,
	};
};

const mapDispatchToProps = {
	fetchProducts: formActions.gateway,
	toggleFlag: formActions.gateway,
	deleteProduct: formActions.gateway,
	fetchStoresList: formActions.gateway,
	toggleAlert: modalActions?.toggleAlert,
	toggleExportFiltersDrawer: drawerActions.toggleExportFiltersDrawer,
	fetchUserList: authActions?.fetchUserList,
	toggleUploadDataModal: modalActions?.toggleUploadDataModal,
	toggleMultiRecipeEditModal: modalActions?.toggleMultiRecipeEditModal,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(CookingRecipeListPage);
